import { eachDayOfInterval, format } from "date-fns";
import { Check, X } from "lucide-react";
import {
  type FC,
  type MouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react";

import CustomChevronIcon from "../ui/CustomChervonIcon";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils/core/generalUtils";

/**
 * DateDropdownFilter Component
 *
 * A dropdown filter that displays individual dates from a fortnight range.
 * Users can select a specific date from the fortnight period.
 * @example
 * // In WpxTable filters configuration:
 * filters={{
 *   fortnight: true,
 *   selectedDate: {
 *     label: "Date",
 *     isDateDropdown: true,
 *   },
 * }}
 */
interface DateDropdownFilterProps {
  label?: string;
  placeholder?: string;
  selectedDate?: string;
  onDateChange: (date: string | null) => void;
  fortnightDates?: Date[];
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  disabled?: boolean;
}

const DateDropdownFilter: FC<DateDropdownFilterProps> = ({
  label = "Select Date",
  placeholder = "Select a date",
  selectedDate,
  onDateChange,
  fortnightDates = [],
  className,
  side = "bottom",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  // Generate date options from fortnight dates
  const dateOptions = useMemo(() => {
    if (!fortnightDates || fortnightDates.length === 0) {
      return [];
    }

    // If we have exactly 2 dates (start and end), generate all dates in between
    if (fortnightDates.length === 2) {
      const [startDate, endDate] = fortnightDates;
      const allDates = eachDayOfInterval({
        start: startDate,
        end: endDate,
      });

      return allDates.map((date) => ({
        value: format(date, "yyyy-MM-dd"),
        label: format(date, "eee, dd MMM"),
        date,
        // Add searchable text for CMDK filtering
        searchText: [
          format(date, "eee, dd MMM"), // "Mon, 15 Jan"

          // these are not needed for now
          // format(date, "yyyy-MM-dd"), // "2024-01-15"
          // format(date, "EEEE"), // "Monday"
          // format(date, "MMMM"), // "January"
          // format(date, "d"), // "15"
        ].join(" "),
      }));
    }

    // If we have individual dates, use them directly
    return fortnightDates.map((date) => ({
      value: format(date, "yyyy-MM-dd"),
      label: format(date, "eee, dd MMM"),
      date,
      // Add searchable text for CMDK filtering
      searchText: [
        format(date, "eee, dd MMM"), // "Mon, 15 Jan"

        // these are not needed for now
        // format(date, "yyyy-MM-dd"), // "2024-01-15"
        // format(date, "EEEE"), // "Monday"
        // format(date, "MMMM"), // "January"
        // format(date, "d"), // "15"
      ].join(" "),
    }));
  }, [fortnightDates]);

  // Find the selected date option
  const selectedOption = useMemo(() => {
    if (!selectedDate) {
      return null;
    }

    return dateOptions.find((option) => option.value === selectedDate);
  }, [selectedDate, dateOptions]);

  const handleSelect = (value: string): void => {
    if (value === selectedDate) {
      // If clicking the same date, clear the selection
      onDateChange(null);
    } else {
      onDateChange(value);
    }
    setOpen(false);
  };

  const handleClear = (e: MouseEvent): void => {
    e.stopPropagation();
    onDateChange(null);
  };

  // Handle popover open/close
  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
  }, []);

  const hasValidTriggerValue =
    selectedOption ||
    (selectedDate &&
      dateOptions.find((option) => option.value === selectedDate));

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className={cn(
            "justify-between hover:bg-secondary min-w-44 max-w-48 h-10",
            side !== "right" && "dashed-border",
            className,
          )}
          disabled={disabled}
        >
          <span
            className={cn(
              "truncate font-normal",
              !hasValidTriggerValue && "opacity-30",
            )}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <div className='flex items-center gap-1'>
            {hasValidTriggerValue ? (
              <X
                className='ml-2 h-4 w-4 opacity-50 hover:text-red-500'
                onClick={handleClear}
              />
            ) : (
              <CustomChevronIcon
                className='ml-2 h-4 w-4 opacity-50'
                side={side}
              />
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[200px] p-0' side={side} align='start'>
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />

          <CommandList>
            <CommandEmpty>No dates found.</CommandEmpty>
            <CommandGroup>
              {dateOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.searchText}
                  onSelect={() => handleSelect(option.value)}
                  className={cn(
                    "cursor-pointer",
                    selectedDate === option.value && "bg-accent",
                  )}
                >
                  <div className='flex items-center justify-between w-full'>
                    <span>{option.label}</span>
                    {selectedDate === option.value && (
                      <Check className='h-4 w-4' />
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default DateDropdownFilter;
